import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ArrowLeft, Clock, Eye, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import { blogPosts } from '../data/mock';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      gsap.fromTo(
        contentRef.current.children,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, stagger: 0.1, duration: 0.8, ease: 'power3.out', delay: 0.2 }
      );
    }

    // Scroll to top
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-32">
        <div className="text-center">
          <h1 className="font-headline text-4xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[var(--primary)] hover:brightness-110">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  return (
    <div className="blog-post-page pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-end">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${post.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--background)] via-[var(--background)]/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 pb-12 w-full">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[var(--primary)] font-headline text-sm font-bold uppercase tracking-code mb-8 hover:gap-3 transition-all group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </Link>

          <span className="inline-block px-3 py-1 bg-[var(--secondary-container)] text-[var(--on-secondary-container)] font-body text-xs tracking-code uppercase mb-4 rounded-sm">
            {post.category}
          </span>

          <h1 className="font-headline text-4xl md:text-6xl font-bold tracking-headline leading-tight mb-6">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-[var(--on-surface-variant)] text-sm">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[var(--primary)] rounded-full flex items-center justify-center font-headline font-bold text-[var(--on-primary)]">
                {post.author.charAt(0)}
              </div>
              <div>
                <p className="font-headline font-bold text-[var(--on-surface)]">{post.author}</p>
                <p className="text-xs">{post.authorRole}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={16} />
              {post.readTime} min read
            </div>
            <div className="flex items-center gap-1">
              <Eye size={16} />
              {post.views} views
            </div>
            <div>{post.publishedDate}</div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section ref={contentRef} className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        {/* Share Buttons */}
        <div className="flex items-center gap-4 mb-12 pb-8 border-b border-[var(--outline-variant)]/10">
          <span className="font-headline text-sm font-bold uppercase tracking-code text-[var(--on-surface-variant)]">
            Share:
          </span>
          <button className="w-10 h-10 bg-[var(--surface-container)] hover:bg-[#1DA1F2] flex items-center justify-center transition-colors group">
            <Twitter size={18} className="text-[var(--on-surface)] group-hover:text-white" />
          </button>
          <button className="w-10 h-10 bg-[var(--surface-container)] hover:bg-[#0077B5] flex items-center justify-center transition-colors group">
            <Linkedin size={18} className="text-[var(--on-surface)] group-hover:text-white" />
          </button>
          <button className="w-10 h-10 bg-[var(--surface-container)] hover:bg-[#1877F2] flex items-center justify-center transition-colors group">
            <Facebook size={18} className="text-[var(--on-surface)] group-hover:text-white" />
          </button>
          <button className="w-10 h-10 bg-[var(--surface-container)] hover:bg-[var(--primary)] flex items-center justify-center transition-colors group">
            <Share2 size={18} className="text-[var(--on-surface)] group-hover:text-[var(--on-primary)]" />
          </button>
        </div>

        {/* Article Content */}
        <div
          className="prose prose-invert max-w-none font-body text-[var(--on-surface-variant)] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.content }}
          style={{
            fontSize: '1.125rem',
            lineHeight: '1.8'
          }}
        />

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-[var(--outline-variant)]/10">
          {post.tags.map((tag, index) => (
            <span
              key={index}
              className="px-4 py-2 bg-[var(--surface-container)] text-[var(--on-surface-variant)] text-sm font-headline font-bold uppercase tracking-code hover:bg-[var(--primary)] hover:text-[var(--on-primary)] transition-colors cursor-pointer"
            >
              {tag}
            </span>
          ))}
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-24 bg-[var(--surface-container-lowest)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-headline text-3xl md:text-4xl font-bold tracking-headline uppercase mb-12">
              Related <span className="text-[var(--primary)]">Articles</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] transition-all duration-500"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-headline text-lg font-bold mb-2 line-clamp-2 group-hover:text-[var(--primary)] transition-colors">
                      {relatedPost.title}
                    </h3>
                    <p className="font-body text-[var(--on-surface-variant)] text-sm line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
